import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

export default function ChangePassword() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const forced = user?.must_change_password;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      updateUser(data.user);
      navigate(data.user.role === "admin" ? "/admin" : "/teacher", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg p-8">
          <div className="h-11 w-11 rounded-lg bg-primary flex items-center justify-center mb-6">
            <ShieldCheck className="h-6 w-6 text-primary-foreground" />
          </div>
          <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground">
            {forced ? "Set a new password" : "Change password"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {forced
              ? "For security, please change the default password before continuing."
              : "Update your account password."}
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="current">Current password</Label>
              <Input
                id="current"
                data-testid="current-password-input"
                className="h-12"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">New password</Label>
              <Input
                id="new"
                data-testid="new-password-input"
                className="h-12"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                data-testid="confirm-password-input"
                className="h-12"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>

            {error && (
              <div
                data-testid="change-password-error"
                className="text-sm rounded-lg bg-destructive/10 text-destructive px-4 py-3 border border-destructive/20"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="password-change-submit"
              disabled={loading}
              className="w-full h-12 text-base"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update Password"}
            </Button>

            {forced && (
              <button
                type="button"
                data-testid="change-password-logout"
                onClick={async () => {
                  await logout();
                  navigate("/login", { replace: true });
                }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Sign out instead
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
